import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EnvironmentInjector,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {combineLatest, Subscription} from "rxjs";
import {NetscopeApiService} from "@app/services/netscope-api.service";
import {NetscopeUiService} from "@app/services/netscope-ui.service";
import {FlowsRepository} from "@app/netscope/model/flows-repository";
import {
  AppsGroupMembersDisplayMode,
  AppsGroups,
  ComboboxSelectionItem,
  defaultDisplayMode,
  FlowDetail,
  FlowRessourceObject,
  IncludedAppsGroupBoxState,
  MemberRoles,
  ResolvedAppsGroupsInclusion,
  TemporalParameter
} from "@app/netscope/model/data-models";
import {AppsGroupFlowsStatus, FlowViewHost} from "@app/netscope/netscope-common-flowview/flowview-host";
import {
  AppGroupState,
  AppsGroupsViewState,
  applyDisplayMode,
  FlowsRendererInterface,
  toggleAppsGroupFolding,
  toggleIncludedAppsGroupBox,
  appsGroupNestingDepth,
  applyAppsGroupsNestingLevel,
  applyIncludedAppsGroupNestingLevel,
  findResolvedInclusion,
  includedAppsGroupKey,
  includedAppsGroupNestingDepth,
  memberVisibilityKey
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview.component";

import {AppsGroupsRendererJointJs} from "@app/netscope/netscope-common-flowview/renderers/AppsGroupsRendererJointJs";
import {AppsGroupsExportFormat} from "@app/netscope/netscope-common-flowview/misc/AppsGroupsGraphExport";
import {VisualisationDataForRenderers} from "@app/netscope/netscope-common-flowview/renderers/ClustersViewerRenderer";
import {AppsGroupsLinksMode} from "@app/netscope/netscope-common-flowview/misc/FlowsDescriptionsClassification";
import {
  NetscopeCommonFlowviewLegendComponent
} from "@app/netscope/netscope-common-flowview/netscope-common-flowview-legend/netscope-common-flowview-legend.component";

/**
 * Map of the apps groups of one file and of the flows between them, drawn by the apps groups renderer.
 *
 * Unlike the flows view, it reads nothing from the URL and loads no apps groups: the page gives it the groups
 * of the displayed file and their display state, which it holds itself so that its own controls and the map
 * change the same state.
 */
@Component({
  standalone: false,
  selector: 'app-apps-groups-map',
  templateUrl: './apps-groups-map.component.html',
  styleUrls: ['./apps-groups-map.component.css']
})
export class AppsGroupsMapComponent implements FlowViewHost, OnInit, OnChanges, OnDestroy {

  // Apps groups of the displayed file, their inclusions resolved (see loadAppsGroupsFile)
  @Input() appsGroups: AppsGroups[] = [];
  @Input() file: string;
  // Display state of the file, held by the page
  @Input() viewState: AppsGroupsViewState = new AppsGroupsViewState();
  // The cog of an apps group box was clicked
  @Output() editAppsGroup: EventEmitter<string> = new EventEmitter();
  // An apps group included in one of the map, which may be in another file
  @Output() editIncludedAppsGroup: EventEmitter<{ name: string, file: string }> = new EventEmitter();
  // The map changed the display state itself (a display mode picked on a box, a box opened or closed)
  @Output() viewStateChanged: EventEmitter<void> = new EventEmitter();

  @ViewChild('legend')
  legend: NetscopeCommonFlowviewLegendComponent;

  appRef = inject(ApplicationRef);
  injector = inject(EnvironmentInjector);

  // DATA
  flowsRepository: FlowsRepository;
  datatableTabFlowsRepository: FlowsRepository;
  filteredLinks: FlowDetail[] = [];
  currentComboboxSelection: ComboboxSelectionItem[] = [];
  temporalParameter: TemporalParameter;
  lastDataUuid: string;
  // Roles of the resources in the flows, set by the renderer as it draws, and read by the page to apply a
  // display mode from its own controls
  memberRoles: Map<string, MemberRoles> = new Map();

  // RENDERING
  renderer: FlowsRendererInterface;
  rendererVersion = 0;
  selectedView = "apps-groups";
  isDarkMode = false;
  isSlow = false;
  // The links are routed precisely unless the user picks a faster routing
  routingAlgorithm = "libavoid";
  currentRoutingAlgorithm = "";
  linksRouting = false;
  reComputingPositionsRequested = false;
  stopComputingPositionsRequested = false;
  isComputingPositions = false;
  draggableNodesEnabled = false;
  graphParameters = {userMovedMap: false, lastTransformation: undefined, firstTransformation: undefined};
  ignoredPorts: number[] = [];
  showIgnoredPorts = false;
  showIndirectLinks = "yes";
  clusterViewerVisibleResourcesOptions = {infrastructure: true, external_ips: true, unknown_ips: false, internet: false};
  clusterViewerShowASNs = "asns";
  showInternalFlowsAppsGroups = false;
  appsGroupsLinksMode: AppsGroupsLinksMode = "ignore";
  // The groups are drawn whatever their flows, the ones no flow reaches set apart below the others: a group just
  // defined would otherwise not show up at all. The user can hide them to see the flows alone.
  showAppsGroupsWithoutFlows = true;
  // Set by the renderer as it draws the groups
  appsGroupsFlowsStatus: Map<string, AppsGroupFlowsStatus> = new Map();
  // The map opens on the groups alone, each one unfolded on demand to see what it is made of, unless the user
  // asked for a number of levels to be unfolded (see nestingLevel)
  appsGroupsFoldedByDefault = true;
  // Number of levels the groups are unfolded by when they are first drawn: 0 for folded, 0.5 to see the groups
  // they include as closed boxes, 1 to see their members as well, 2 to open these boxes, and so on. The map
  // always opens on the groups they include, the level picked is not kept from one visit to the next
  nestingLevel: number = 0.5;

  // SELECTION
  // Apps group clicked on the map, shown in the bar at its bottom, and the box of an included apps group
  // clicked inside of it, by key (see registerIncludedBoxes): none when the apps group itself was clicked
  selectedAppsGroupName: string;
  selectedIncludedAppsGroupKey: string;

  // FLOWS SIDE PANEL
  sidePanelOpened = false;
  sidePanelTitle = "";
  sidePanelRepository: FlowsRepository;
  sidePanelResourceUuid: string;

  // REPORTS
  // The reports open this page in a headless browser, wait for 'data-render-ready' on the map and read its
  // files through 'window.netscopeAppsGroupsMap' (see registerReportsHooks)
  private drawingNumber = 0;
  private drawn = false;
  private drawingFailed = false;

  @HostBinding("attr.data-render-ready")
  get renderReadyAttribute(): string {
    // The links are routed once the drawing is done (see AvoidRouter.routeAllSoon)
    return this.drawn && !this.linksRouting ? "" : null;
  }

  @HostBinding("attr.data-render-error")
  get renderErrorAttribute(): string {
    return this.drawingFailed ? "" : null;
  }

  private subscriptions: Subscription[] = [];

  constructor(public netscopeService: NetscopeApiService, private netscopeUiService: NetscopeUiService,
              private ref: ChangeDetectorRef) {
  }

  // The renderer reads the apps groups and their display state under the names the flows view gives them
  get groupsApps(): AppsGroups[] {
    return this.appsGroups;
  }

  get appsGroupsMode(): string {
    return this.file;
  }

  get appGroupStates(): Map<string, AppGroupState> {
    return this.viewState.appGroupStates;
  }

  get vmVisibilityStates(): Map<string, boolean> {
    return this.viewState.vmVisibilityStates;
  }

  get hiddenAppsGroups(): Set<string> {
    return this.viewState.hiddenAppsGroups;
  }

  ngOnInit(): void {
    this.registerReportsHooks();

    this.subscriptions.push(combineLatest([
      this.netscopeUiService.comboboxSelectionItemSubject,
      this.netscopeUiService.duckDbRefinedDataSubject,
    ]).subscribe(([comboboxSelectionItems, duckDbData]) => {
      this.flowsRepository = duckDbData.flowsRepository.clone();
      this.currentComboboxSelection = comboboxSelectionItems;

      if (this.lastDataUuid !== duckDbData.dataUuidAndWhere) {
        this.lastDataUuid = duckDbData.dataUuidAndWhere;
        this.reloadUi();
      }
    }));

    this.subscriptions.push(this.netscopeUiService.temporalParametersSubject.subscribe((temporalParameter) => {
      this.temporalParameter = temporalParameter;
    }));

    // The first value is the current theme, the map is drawn with it by the data subscription above
    let firstDarkModeStatus = true;
    this.subscriptions.push(this.netscopeUiService.darkModeStatusSubject.subscribe((isDarkMode: boolean) => {
      this.isDarkMode = isDarkMode;
      if (firstDarkModeStatus) {
        firstDarkModeStatus = false;
      } else {
        this.reloadUi();
      }
    }));

    // Nothing is done when the window is resized: the paper of the renderer fills its element, and laying the
    // whole graph out again at each step of a resize would redraw it dozens of times
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The selection belongs to the file it was made in, and to the groups as they still are
    if (changes['file'] && !changes['file'].firstChange) {
      this.selectAppsGroup(undefined);
    }
    if (changes['appsGroups'] && !changes['appsGroups'].firstChange) {
      if (this.selectedAppsGroup == undefined) {
        this.selectAppsGroup(undefined);
      } else if (this.selectedIncludedAppsGroupKey != undefined && this.selectedInclusion == undefined) {
        this.selectedIncludedAppsGroupKey = undefined;
      }
      this.reloadUi();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.destroyRenderer();
    if ((window as any).netscopeAppsGroupsMap?.map === this) {
      delete (window as any).netscopeAppsGroupsMap;
    }
  }

  /**
   * Draws the map again from scratch: the renderer reads the apps groups and their display state back from
   * here. Nothing is drawn until both the flows and the apps groups are known.
   */
  reloadUi = () => {
    let previousRenderer = this.renderer;
    this.destroyRenderer();

    if (this.flowsRepository == undefined || this.appsGroups == undefined || this.file == undefined) {
      return;
    }

    // The groups drawn for the first time are unfolded by the levels the user asked for
    applyAppsGroupsNestingLevel(this, this.appsGroups, this.nestingLevel, false);

    this.isSlow = false;
    let renderer = new AppsGroupsRendererJointJs();
    // The flows already loaded are drawn again rather than loaded anew, when they are still the ones to draw
    renderer.takeLoadedDataFrom(previousRenderer);
    this.renderer = renderer;
    this.draw();
  }

  // Draws the map with the current renderer, telling the reports when it is done (see renderReadyAttribute)
  private draw() {
    let drawing = ++this.drawingNumber;
    this.drawn = false;
    this.drawingFailed = false;
    Promise.resolve(this.renderer.render(this)).then(() => {
      if (drawing == this.drawingNumber) {
        this.drawn = true;
      }
    }, (error) => {
      console.error("[apps-groups] the map could not be drawn", error);
      if (drawing == this.drawingNumber) {
        this.drawingFailed = true;
      }
    });
  }

  /**
   * Lets the reports read the files the export buttons download: an SVG or a draw.io diagram as text, a PNG
   * encoded in base64, nothing when the map holds nothing. Called by the headless browser once the map is
   * drawn, as 'await window.netscopeAppsGroupsMap.export("svg")'.
   */
  private registerReportsHooks() {
    (window as any).netscopeAppsGroupsMap = {
      map: this,
      export: async (format: AppsGroupsExportFormat): Promise<string> => {
        if (!(this.renderer instanceof AppsGroupsRendererJointJs)) {
          return undefined;
        }
        let blob = await this.renderer.exportToBlob(format);
        if (blob == undefined) {
          return undefined;
        }
        if (format != "png") {
          return await blob.text();
        }
        let bytes = new Uint8Array(await blob.arrayBuffer());
        let binary = "";
        for (let i = 0; i < bytes.length; i += 0x8000) {
          binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
        }
        return btoa(binary);
      },
    };
  }

  private destroyRenderer() {
    // A drawing still going on no longer counts, and the map is not ready until drawn again
    this.drawingNumber++;
    this.drawn = false;
    if (this.renderer != undefined) {
      this.renderer.destroy(this);
      this.renderer = undefined;
    }
  }

  askRefreshAngular = () => {
    this.ref.detectChanges();
  }

  updateLegend(visualisationData: VisualisationDataForRenderers) {
    this.legend?.updateLegend(this.selectedView, visualisationData);
  }

  zoomIn = () => this.renderer?.zoomInVisualisation(this);
  zoomOut = () => this.renderer?.zoomOutVisualisation(this);
  recenter = () => this.renderer?.recenterVisualisation(this);

  exportAs = (format: AppsGroupsExportFormat) => {
    this.renderer?.exportAs?.(format);
  }

  // FLOWS SIDE PANEL

  askShowSidePanel = (title: string, where: string, resourceId: string = undefined,
                      sidePanelObject: FlowRessourceObject = undefined) => {
    this.sidePanelRepository = this.flowsRepository.clone();
    this.sidePanelRepository.setWhereClause(where);
    this.sidePanelRepository.aggregated_flows_files = this.sidePanelRepository.flows_files;
    this.sidePanelRepository.showFirstPage();
    this.sidePanelTitle = title;
    this.sidePanelResourceUuid = resourceId;
    this.sidePanelOpened = true;
    this.ref.detectChanges();
  }

  // Only the flows view hands flows over directly, the map always asks for them with a where clause
  showSidePanel = (title: string, flowsDetails: FlowDetail[], resourceId: string = undefined,
                   sidePanelObject: FlowRessourceObject = undefined) => {
  }

  switchFocus = (vm, mode = "focus") => {
    this.sidePanelOpened = false;
    this.netscopeUiService.switchFocus(vm, mode);
  }

  // APPS GROUPS BOXES

  showAppGroupSettings = (appGroup: FlowRessourceObject) => {
    this.editAppsGroup.emit(appGroup.name);
  }

  showIncludedAppsGroupSettings = (ref: { name: string, file: string }) => {
    this.editIncludedAppsGroup.emit(ref);
  }

  applyAppGroupMode = (appGroup: AppsGroups, mode: AppsGroupMembersDisplayMode) => {
    let appGroupState = this.appGroupStates.get(appGroup?.name);
    if (appGroupState == undefined) {
      return;
    }
    applyDisplayMode(appGroupState, mode, appGroup.vms ?? [], appGroup.included_apps_groups ?? [],
                     this.vmVisibilityStates, this.memberRoles);
    this.viewStateChanged.emit();
  }

  // Same as the flows view: the choice is kept with the display state of the including group
  toggleIncludedAppsGroup = (inclusion: IncludedAppsGroupBoxState) => {
    toggleIncludedAppsGroupBox(this.appGroupState(inclusion.includingAppsGroupName), this.vmVisibilityStates,
                               inclusion.key, inclusion);

    this.renderer.destroy(this);
    this.draw();
    this.viewStateChanged.emit();
  }

  // Display state of an apps group, created as the panel would if the user made no choice on the group yet
  private appGroupState(appGroupName: string): AppGroupState {
    if (!this.appGroupStates.has(appGroupName)) {
      let appGroup = this.appsGroups.find((candidate) => candidate.name == appGroupName);
      let appGroupState: AppGroupState = new AppGroupState();
      appGroupState.mode = appGroup?.mode ?? defaultDisplayMode(appGroup);
      appGroupState.appGroup = appGroup;
      appGroupState.uuid = appGroup?.uuid;
      appGroupState.name = appGroupName;
      this.appGroupStates.set(appGroupName, appGroupState);
    }
    return this.appGroupStates.get(appGroupName);
  }

  // ---------------------------------------------------------------- Groups without flows

  // Statuses of the groups the renderer went through, the boxes of included groups left out
  private get groupsFlowsStatuses(): AppsGroupFlowsStatus[] {
    return (this.appsGroups ?? [])
      .map((appGroup) => this.appsGroupsFlowsStatus.get(appGroup.uuid))
      .filter((status) => status != undefined);
  }

  get groupsWithoutFlowsCount(): number {
    return this.groupsFlowsStatuses.filter((status) => status != "flows").length;
  }

  // None of the groups exchanged a flow with another one: the map alone would leave the user wondering why
  get noGroupHasFlows(): boolean {
    let statuses = this.groupsFlowsStatuses;
    return statuses.length > 0 && statuses.every((status) => status != "flows");
  }

  toggleAppsGroupsWithoutFlows = () => {
    this.showAppsGroupsWithoutFlows = !this.showAppsGroupsWithoutFlows;
    this.reloadUi();
  }

  // ---------------------------------------------------------------- Nesting level

  // Levels the selector offers: as many as the groups of the file can be unfolded by
  get nestingLevels(): number[] {
    let deepest = Math.max(1, ...(this.appsGroups ?? []).map((appGroup) => appsGroupNestingDepth(appGroup)));
    let count = Math.max(deepest, Math.ceil(this.nestingLevel)) + 1;
    let levels = Array.from({length: count}, (_, level) => level);
    levels.splice(1, 0, 0.5);
    return levels;
  }

  nestingLevelLabel = (level: number): string => {
    if (level == 0) {
      return "0 · folded";
    }
    return level == 0.5 ? "0.5 · groups only" : `${level}`;
  }

  // Unfolds every group by the levels picked, whatever was folded or unfolded by hand
  onNestingLevelChange = (level: number) => {
    this.nestingLevel = level;
    applyAppsGroupsNestingLevel(this, this.appsGroups ?? [], level, true);
    this.reloadUi();
    this.viewStateChanged.emit();
  }

  // ---------------------------------------------------------------- Selected apps group

  get selectedAppsGroup(): AppsGroups {
    return this.selectedAppsGroupName == undefined
      ? undefined
      : (this.appsGroups ?? []).find((appGroup) => appGroup.name == this.selectedAppsGroupName);
  }

  // Included apps group the selected box stands for, none when an apps group itself is selected
  get selectedInclusion(): ResolvedAppsGroupsInclusion {
    return this.selectedIncludedAppsGroupKey == undefined
      ? undefined
      : findResolvedInclusion(this.selectedAppsGroup, this.selectedIncludedAppsGroupKey);
  }

  selectAppsGroup = (appGroup: AppsGroups | undefined, includedKey: string = undefined) => {
    if (appGroup?.name == this.selectedAppsGroupName && includedKey == this.selectedIncludedAppsGroupKey) {
      return;
    }
    this.selectedAppsGroupName = appGroup?.name;
    this.selectedIncludedAppsGroupKey = appGroup == undefined ? undefined : includedKey;
    this.ref.detectChanges();
  }

  // Groups the selected box is drawn in, from the apps group down to the box itself
  get selectionPath(): string[] {
    let path = [this.selectedAppsGroupName];
    let inclusions = this.selectedAppsGroup?.resolvedInclusions ?? [];
    for (let part of (this.selectedIncludedAppsGroupKey ?? "").split(">").filter((part) => part != "")) {
      let inclusion = inclusions.find((candidate) => includedAppsGroupKey(candidate) == part);
      path.push(inclusion?.name ?? part);
      inclusions = inclusion?.inclusions ?? [];
    }
    return path;
  }

  // What the selection holds, as its folded box sums it up
  get selectionSummary(): string {
    let inclusion = this.selectedInclusion;
    let membersCount = inclusion != undefined ? inclusion.members.length : (this.selectedAppsGroup?.vms ?? []).length;
    let groupsCount = inclusion != undefined
      ? (inclusion.inclusions ?? []).length
      : (this.selectedAppsGroup?.resolvedInclusions ?? []).length;
    let parts = [membersCount == 1 ? "1 member" : `${membersCount} members`];
    if (groupsCount > 0) {
      parts.push(groupsCount == 1 ? "1 group inside" : `${groupsCount} groups inside`);
    }
    return parts.join(" · ");
  }

  /**
   * How the selection is drawn, as far as its nesting goes: whether it is folded, the boxes opened inside of it
   * and the members shown. Two levels drawing it the same have the same signature.
   */
  private selectionSignature(host: { appGroupStates: Map<string, AppGroupState>, vmVisibilityStates: Map<string, boolean> },
                             appGroup: AppsGroups, key: string, inclusion: ResolvedAppsGroupsInclusion): string {
    let state = host.appGroupStates.get(appGroup.name);
    let expandedKeys = Array.from(state?.expandedIncludedAppsGroups ?? [])
      .filter((candidate) => key == undefined || candidate == key || candidate.startsWith(`${key}>`))
      .sort();
    let members = inclusion != undefined ? inclusion.members : (appGroup.vms ?? []);
    let shownUuids = members.map((member) => member.uuid)
      .filter((uuid) => host.vmVisibilityStates.get(memberVisibilityKey(appGroup.name, uuid)) == true)
      .sort();
    // A box is only drawn in its apps group unfolded, whether the group is folded only tells the group apart
    let folded = key == undefined ? (state?.folded ?? this.appsGroupsFoldedByDefault) : false;
    return JSON.stringify([folded, expandedKeys, shownUuids]);
  }

  // Signature of the selection once unfolded by a level, worked out on a display state of its own
  private selectionSignatureAt(level: number): string {
    let appGroup = this.selectedAppsGroup;
    let key = this.selectedIncludedAppsGroupKey;
    let host = {
      appsGroupsMode: this.file,
      hiddenAppsGroups: new Set<string>(),
      appGroupStates: new Map<string, AppGroupState>(),
      vmVisibilityStates: new Map<string, boolean>(),
      appsGroupsFoldedByDefault: this.appsGroupsFoldedByDefault,
    } as unknown as FlowViewHost;
    if (key == undefined) {
      applyAppsGroupsNestingLevel(host, [appGroup], level, true);
    } else {
      applyIncludedAppsGroupNestingLevel(host, appGroup, key, level);
    }
    return this.selectionSignature(host, appGroup, key, this.selectedInclusion);
  }

  /**
   * Levels the selection can be unfolded by: half a level only when it includes groups, and no more levels than
   * it holds (see appsGroupNestingDepth). A level drawing it as the one before does is left out: a group with no
   * members of its own is drawn the same at half a level and at one, a step would then change nothing.
   */
  get selectionNestingLevels(): number[] {
    let appGroup = this.selectedAppsGroup;
    let inclusion = this.selectedInclusion;
    if (appGroup == undefined || (this.selectedIncludedAppsGroupKey != undefined && inclusion == undefined)) {
      return [];
    }
    let depth = inclusion != undefined ? includedAppsGroupNestingDepth(inclusion) : appsGroupNestingDepth(appGroup);
    let candidates = [0, 0.5].concat(Array.from({length: depth}, (_, index) => index + 1));

    let levels: number[] = [];
    let lastSignature: string = undefined;
    for (let level of candidates) {
      let signature = this.selectionSignatureAt(level);
      if (signature != lastSignature) {
        levels.push(level);
        lastSignature = signature;
      }
    }
    return levels;
  }

  // The level the selection is drawn at, 'custom' when it was folded or unfolded by hand to match none of them
  get selectionNestingLevel(): number | "custom" {
    let appGroup = this.selectedAppsGroup;
    if (appGroup == undefined) {
      return "custom";
    }
    let current = this.selectionSignature(this, appGroup, this.selectedIncludedAppsGroupKey, this.selectedInclusion);
    return this.selectionNestingLevels.find((level) => this.selectionSignatureAt(level) == current) ?? "custom";
  }

  // Unfolds the selection by a number of levels, whatever was folded or unfolded in it by hand
  setSelectionNestingLevel = (level: number) => {
    let appGroup = this.selectedAppsGroup;
    if (appGroup == undefined) {
      return;
    }
    let key = this.selectedIncludedAppsGroupKey;
    if (key == undefined) {
      applyAppsGroupsNestingLevel(this, [appGroup], level, true);
    } else {
      applyIncludedAppsGroupNestingLevel(this, appGroup, key, level, this.selectedBoxMemberUuids);
    }

    this.renderer.destroy(this);
    this.draw();
    this.viewStateChanged.emit();
  }

  // A selection folded or unfolded by hand matches no level: a step starts from the folded one
  stepSelectionNestingLevel = (step: number) => {
    let levels = this.selectionNestingLevels;
    if (levels.length == 0) {
      return;
    }
    let current = this.selectionNestingLevel;
    let index = current == "custom" ? (step > 0 ? 0 : 1) : levels.indexOf(current);
    let next = levels[Math.min(levels.length - 1, Math.max(0, index + step))];
    if (next != current) {
      this.setSelectionNestingLevel(next);
    }
  }

  /**
   * Whether the flows inside the selected apps group are drawn, between the members and the boxes of included
   * apps groups shown in it. It is a choice made on the whole apps group, the one the panel makes as well: a box
   * selected inside of it shows the choice of its apps group.
   */
  get selectionShowsInternalFlows(): boolean {
    return this.appGroupStates.get(this.selectedAppsGroupName)?.showInternalFlows ?? false;
  }

  toggleSelectionInternalFlows = () => {
    if (this.selectedAppsGroupName == undefined) {
      return;
    }
    let state = this.appGroupState(this.selectedAppsGroupName);
    state.showInternalFlows = !state.showInternalFlows;

    this.renderer.destroy(this);
    this.draw();
    this.viewStateChanged.emit();
  }

  // Resources the selected box was drawn with, the networks it holds resolved into the resources they gathered
  private get selectedBoxMemberUuids(): string[] {
    if (!(this.renderer instanceof AppsGroupsRendererJointJs) || this.selectedIncludedAppsGroupKey == undefined) {
      return [];
    }
    let box = (this.renderer.includedBoxesByAppGroupUuid.get(this.selectedAppsGroup?.uuid) ?? [])
      .find((candidate) => candidate.key == this.selectedIncludedAppsGroupKey);
    return Array.from(box?.memberUuids ?? []);
  }

  // The flows of the selection, as a double click on the box of an apps group shows them
  showSelectionFlows = () => {
    let appGroup = this.selectedAppsGroup;
    if (appGroup == undefined || !(this.renderer instanceof AppsGroupsRendererJointJs)) {
      return;
    }
    // The members as they were drawn, the networks resolved into the resources they gathered
    let memberUuids = this.selectedIncludedAppsGroupKey == undefined
      ? (this.renderer.membersByAppsGroupName.get(appGroup.name) ?? []).map((member) => member.uuid)
      : this.selectedBoxMemberUuids;
    let whereClauses = ["false"].concat(memberUuids.map((uuid) =>
      ` (src_resource_uuid = '${uuid}' or dst_resource_uuid = '${uuid}') `));
    this.askShowSidePanel(this.selectionPath.join(" › "), "where " + whereClauses.join(" or "), appGroup.uuid);
  }

  // An included apps group is edited in the file holding it, as the cog of its box does
  editSelection = () => {
    let inclusion = this.selectedInclusion;
    if (inclusion != undefined) {
      this.editIncludedAppsGroup.emit({name: inclusion.name, file: inclusion.file ?? this.file});
    } else if (this.selectedAppsGroupName != undefined) {
      this.editAppsGroup.emit(this.selectedAppsGroupName);
    }
  }

  // '+' and '-' unfold and fold the selection, Escape clears it, unless the user is typing
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.selectedAppsGroup == undefined || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    let target = event.target as HTMLElement;
    if (target?.closest?.("input, textarea, select, [contenteditable]")) {
      return;
    }
    if (event.key == "+" || event.key == "=") {
      this.stepSelectionNestingLevel(1);
    } else if (event.key == "-") {
      this.stepSelectionNestingLevel(-1);
    } else if (event.key == "Escape") {
      this.selectAppsGroup(undefined);
    } else {
      return;
    }
    event.preventDefault();
  }

  // Same as the flows view: the choice is kept with the display state of the group
  toggleAppsGroupFolded = (appGroup: AppsGroups) => {
    if (appGroup == undefined) {
      return;
    }
    toggleAppsGroupFolding(this, appGroup);

    this.renderer.destroy(this);
    this.draw();
    this.viewStateChanged.emit();
  }
}
